import { useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { pageVariants, reducedPageVariants, springs } from '../../animation';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { useSoundEffects } from '../../hooks/useSoundEffects';
import { useHaptic } from '../../hooks/useHaptic';
import { useOnboardingStore } from '../../hooks/useOnboardingStore';
import WelcomeHookScreen from './WelcomeHookScreen';
import WelcomeHowItWorksScreen from './WelcomeHowItWorksScreen';
import WelcomeTrustScreen from './WelcomeTrustScreen';
import WelcomeSignupScreen from './WelcomeSignupScreen';

export default function WelcomeFlow() {
  const { welcomeStep, setWelcomeStep } = useOnboardingStore();
  const { play } = useSoundEffects();
  const haptic = useHaptic();
  const reduced = useReducedMotion();

  const variants = reduced ? reducedPageVariants : pageVariants;

  const advance = useCallback((next: 0 | 1 | 2 | 3) => {
    play('navigate');
    haptic.tap();
    setWelcomeStep(next);
  }, [play, haptic, setWelcomeStep]);

  const screens = [
    <WelcomeHookScreen key="hook" onNext={() => advance(1)} />,
    <WelcomeHowItWorksScreen key="how" onNext={() => advance(2)} />,
    <WelcomeTrustScreen key="trust" onNext={() => advance(3)} />,
    <WelcomeSignupScreen key="signup" />,
  ];

  // Handle swipe gestures
  const handleDragEnd = useCallback((_: any, info: { offset: { x: number }; velocity: { x: number } }) => {
    const swipeThreshold = 50;
    const velocityThreshold = 200;

    if (
      (info.offset.x < -swipeThreshold || info.velocity.x < -velocityThreshold) &&
      welcomeStep < 3
    ) {
      advance((welcomeStep + 1) as 0 | 1 | 2 | 3);
    } else if (
      (info.offset.x > swipeThreshold || info.velocity.x > velocityThreshold) &&
      welcomeStep > 0
    ) {
      advance((welcomeStep - 1) as 0 | 1 | 2 | 3);
    }
  }, [welcomeStep, advance]);

  return (
    <div className="min-h-screen bg-kid-bg texture-paper-grain overflow-hidden relative">
      <AnimatePresence mode="wait">
        <motion.div
          key={welcomeStep}
          variants={variants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={reduced ? { duration: 0.2 } : springs.pageTurn}
          drag={reduced ? false : 'x'}
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.2}
          onDragEnd={handleDragEnd}
        >
          {screens[welcomeStep]}
        </motion.div>
      </AnimatePresence>

      {/* Step dots — active dot is tangerine */}
      <div className="fixed bottom-8 left-1/2 -translate-x-1/2 flex gap-3">
        {[0, 1, 2, 3].map((i) => (
          <button
            key={i}
            onClick={() => {
              if (i < welcomeStep) advance(i as 0 | 1 | 2 | 3);
            }}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              i === welcomeStep
                ? 'bg-kid-tangerine scale-125'
                : i < welcomeStep
                  ? 'bg-kid-tangerine/40 cursor-pointer'
                  : 'bg-kid-tangerine/20'
            }`}
            aria-label={`Step ${i + 1}`}
            aria-current={i === welcomeStep ? 'step' : undefined}
          />
        ))}
      </div>
    </div>
  );
}
